import { useParams, Link, Navigate } from "react-router-dom";
import FlowerButton from "@/components/FlowerButton";
import CollectibleCard from "@/components/CollectibleCard";
import ZoomImage from "@/components/ZoomImage";
import { Sparkles, CloudDrizzle, Ghost, MoonStar, Cookie, Palette, MessageCircleHeart, MapPin, Heart, Users, Home } from "lucide-react";

const TRAIT_ICONS = {
  "Qualités": Sparkles,
  "Défauts": CloudDrizzle,
  "Peur": Ghost,
  "Rêve": MoonStar,
  "Nourriture préférée": Cookie,
  "Hobby": Palette,
  "Phrase récurrente": MessageCircleHeart,
  "Son quartier": MapPin,
};
const traitIcon = (label) => TRAIT_ICONS[label] || (/parents|grands-parents|sœur|frère/i.test(label) ? Home : label.startsWith("Avec") ? Heart : Users);
import ProductCard from "@/components/ProductCard";
import { findCharacter, productsByCharacter, CHARACTERS } from "@/data/mellow";

const TraitList = ({ items, testId, color }) => (
  <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-6" data-testid={testId}>
    {items.map((t) => {
      const Icon = traitIcon(t.label);
      return (
        <div key={t.label} className="flex gap-4">
          <span className="trait-icon shrink-0 w-11 h-11 rounded-full flex items-center justify-center text-[#78353A] bg-[#FFFDF9] border-2 border-dashed" style={{ borderColor: color }} data-testid={`trait-icon-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </span>
          <div>
            <dt className="font-cursive text-2xl text-[#78353A] leading-none mb-1">{t.label}</dt>
            <dd className="text-[#4A3B32]/90 leading-relaxed">{t.value}</dd>
          </div>
        </div>
      );
    })}
  </dl>
);

const CurlyArrow = () => (
  <svg viewBox="0 0 100 100" className="w-16 h-16" aria-hidden="true">
    <path className="hand-arrow" d="M10,20 C 30,10 50,30 70,20 S 90,60 70,80" />
    <path className="hand-arrow" d="M65,72 L 70,80 L 78,74" />
  </svg>
);

export default function Character() {
  const { slug } = useParams();
  const c = findCharacter(slug);
  if (!c) return <Navigate to="/mellowfriends" replace />;
  const idx = CHARACTERS.findIndex((x) => x.slug === slug);
  const products = productsByCharacter(slug);

  return (
    <div style={{ background: c.bg }}>
      {/* Description block */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <p className="font-cursive text-2xl text-[#78353A]/70">By MiniMellow</p>
            <h1 className="font-script text-7xl md:text-8xl text-[#78353A] leading-[0.9]">{c.name}</h1>
            <p className="mt-2 font-cursive text-3xl text-[#4A3B32]">{c.species}</p>
            <p className="mt-2 text-[#4A3B32]/70 italic">{c.tagline}</p>
            <div className="mt-8">
              <CollectibleCard c={c} index={idx} total={CHARACTERS.length} />
            </div>
            <h2 className="font-cursive text-5xl mt-10 mb-3 text-[#78353A]">Description</h2>
            <p className="text-[#4A3B32]/90 leading-relaxed max-w-lg">{c.description}</p>
          </div>
          <div className="relative">
            <img
              src={c.heroImg || c.portrait}
              alt={c.name}
              className={`relative rounded-[2.5rem] animate-float-slow ${!c.heroImg ? "opacity-70" : ""}`}
            />
            {!c.heroImg && (
              <p className="text-center text-xs mt-2 text-[#4A3B32]/50 italic">Illustration à venir</p>
            )}
          </div>
        </div>
      </section>

      {/* Presentation card (optional) */}
      {c.presentationImg && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
          <div className="mx-auto max-w-3xl">
            <ZoomImage src={c.presentationImg} alt={`Présentation de ${c.name}`} data-testid="character-presentation-img" className="rounded-[2rem] w-full" />
          </div>
        </section>
      )}

      {/* Personality */}
      {c.traits ? (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 space-y-14">
          <div className="p-4 md:p-6">
            <h2 className="font-cursive text-5xl text-[#78353A] mb-6">Personnalité</h2>
            {c.personality && <p className="text-[#4A3B32]/90 leading-relaxed max-w-3xl mb-8">{c.personality}</p>}
            <TraitList items={c.traits} testId="character-traits" color={c.color} />
          </div>
          {c.relations && (
            <div className="p-4 md:p-6">
              <h2 className="font-cursive text-5xl text-[#78353A] mb-6">Ses relations</h2>
              <TraitList items={c.relations} testId="character-relations" color={c.color} />
            </div>
          )}
          <div className="p-4 md:p-6">
            <h2 className="font-cursive text-5xl text-[#78353A] mb-4">À l'école...</h2>
            <p className="text-[#4A3B32]/90 leading-relaxed max-w-3xl">{c.school}</p>
          </div>
        </section>
      ) : (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 gap-10 items-start">
        <div className="p-4 md:p-6 relative">
          <h2 className="font-cursive text-5xl text-[#78353A] mb-4">Personnalité</h2>
          <p className="text-[#4A3B32]/90 leading-relaxed">{c.personality}</p>
        </div>
        <div className="p-4 md:p-6">
          <h2 className="font-cursive text-5xl text-[#78353A] mb-4">À l'école...</h2>
          <p className="text-[#4A3B32]/90 leading-relaxed">{c.school}</p>
        </div>
      </section>
      )}

      {/* House */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <div className="p-4 md:p-8">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-cursive text-5xl md:text-6xl text-[#78353A] mb-4">Sa maison</h2>
              <p className="text-[#4A3B32]/90 leading-relaxed">{c.house}</p>
              <p className="mt-4 text-[#4A3B32]/80 leading-relaxed italic">{c.accessory}</p>
            </div>
            {c.houseImg ? (
              <ZoomImage src={c.houseImg} alt={`Maison de ${c.name}`} className="rounded-3xl animate-float w-full" />
            ) : (
              <div className="rounded-3xl bg-[#FAF9F6]/60 h-72 flex items-center justify-center">
                <span className="daisy-deco" style={{ width: 100, height: 100 }}></span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Family (optional) */}
      {c.family && (
        <section className="relative overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
            {c.family.overviewImg && (
              <div className="mx-auto max-w-3xl mb-14">
                <ZoomImage src={c.family.overviewImg} alt={c.family.title} className="rounded-[2rem] w-full" />
              </div>
            )}
            <div className="space-y-16">
              {c.family.groups.map((g, i) => {
                const imgs = g.imgs || [g.img];
                return (
                  <div key={i} className="flex flex-col items-center" data-testid={`family-group-${i}`}>
                    <h3 className="font-cursive text-4xl md:text-5xl text-[#78353A] mb-6 text-center">{g.label}</h3>
                    <div className={`grid gap-8 w-full ${imgs.length > 1 ? "md:grid-cols-2 max-w-6xl" : "max-w-3xl"} mx-auto`}>
                      {imgs.map((src, j) => (
                        <ZoomImage key={j} src={src} alt={g.label} className="rounded-2xl w-full" />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Collection */}
      {products.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="character-collection">
          <p className="font-cursive text-2xl text-[#78353A]/70">La collection {c.name} {c.surname}</p>
          <h2 className="font-cursive text-5xl md:text-6xl text-[#78353A] leading-none mb-8">Sa petite collection</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => <ProductCard key={p.lookup_key} product={p} />)}
          </div>
        </section>
      )}

      {/* Prev / next character */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20 flex flex-wrap justify-between gap-4">
        {(() => {
          const prev = CHARACTERS[(idx - 1 + CHARACTERS.length) % CHARACTERS.length];
          const next = CHARACTERS[(idx + 1) % CHARACTERS.length];
          return (
            <>
              <Link to={`/personnages/${prev.slug}`}><FlowerButton variant="sage">← {prev.name}</FlowerButton></Link>
              <Link to={`/personnages/${next.slug}`}><FlowerButton>{next.name} →</FlowerButton></Link>
            </>
          );
        })()}
      </section>
    </div>
  );
}
